const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const DAY_INDEX = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

export const AVAILABILITY_INTERVAL_MINUTES = 15;
export const DEFAULT_BOOKING_SLOT_INTERVAL_MINUTES = AVAILABILITY_INTERVAL_MINUTES;

const padTimePart = (value) => value.toString().padStart(2, "0");

// The server stores a booking or block's day at UTC midnight and sends it as
// "YYYY-MM-DD", "YYYY-MM-DDT00:00:00" or "YYYY-MM-DDT00:00:00.000Z". All three
// name a calendar day, not an instant: read with local getters, the last one
// lands on the previous day anywhere west of UTC. Only an exact midnight
// counts, so a real instant that happens to carry a date is never mistaken
// for a day.
const CALENDAR_DAY_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:T00:00(?::00(?:\.0{1,3})?)?(?:Z|[+-]00:?00)?)?$/;

export const toDateKey = (value) => {
  if (typeof value !== "string") return null;
  const match = CALENDAR_DAY_PATTERN.exec(value.trim());
  if (!match) return null;
  const [, year, month, day] = match;
  const check = new Date(Number(year), Number(month) - 1, Number(day));
  if (check.getMonth() !== Number(month) - 1 || check.getDate() !== Number(day)) return null;
  return `${year}-${month}-${day}`;
};

// A server day as a local Date at midnight, so formatting and setHours() keep
// it on the right day. Anything that isn't a server day is parsed as-is.
export const parseCalendarDay = (value) => {
  if (!value) return null;
  const key = toDateKey(value);
  const date = key
    ? new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)))
    : value instanceof Date
      ? new Date(value.getTime())
      : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const toISODateString = (value) => {
  if (!value) return null;
  const key = toDateKey(value);
  if (key) return key;
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return [date.getFullYear(), padTimePart(date.getMonth() + 1), padTimePart(date.getDate())].join("-");
};

export const normalizeSlot = (slot) => {
  if (!slot) return null;
  if (typeof slot === "string") {
    const [start, end] = slot.split("-");
    if (start && end) {
      return { start: start.trim(), end: end.trim() };
    }
    return null;
  }
  const start = slot.start?.trim?.() ?? slot.start;
  const end = slot.end?.trim?.() ?? slot.end;
  if (typeof start === "string" && typeof end === "string") {
    return { start, end };
  }
  return null;
};

export const getDayAvailabilityForDate = (availability, isoDate) => {
  if (!isoDate) return null;
  const day = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(day.getTime())) return null;
  const dayName = DAY_NAMES[day.getDay()];
  return availability?.find((entry) => entry.day?.toLowerCase?.() === dayName.toLowerCase());
};

// Weekly lists for one provider folded into one, each weekday keeping the times
// any of them gives it. For deciding which weekdays ever open: a weekday one
// list leaves empty (the /available-slots answer for a booked-out next Thursday,
// say) stays open while another still has hours for it.
export const mergeWeeklyAvailability = (...lists) => {
  const byDay = new Map();
  lists.forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach((entry) => {
      const key = entry?.day?.toLowerCase?.();
      if (!key) return;
      const slots = Array.isArray(entry.slots) ? entry.slots : [];
      const merged = byDay.get(key);
      if (merged) {
        merged.slots = [...merged.slots, ...slots];
      } else {
        byDay.set(key, { day: entry.day, slots });
      }
    });
  });
  return Array.from(byDay.values());
};

export const timeStringToMinutes = (value) => {
  if (typeof value !== "string") return null;
  const [hours, minutes] = value.split(":").map(Number);
  if (
    Number.isInteger(hours) &&
    Number.isInteger(minutes) &&
    hours >= 0 &&
    hours < 24 &&
    minutes >= 0 &&
    minutes < 60
  ) {
    return hours * 60 + minutes;
  }
  return null;
};

export const minutesToTimeString = (minutes) => {
  if (!Number.isFinite(minutes)) return null;
  const clamped = Math.max(0, Math.min(minutes, 24 * 60));
  const hours = Math.floor(clamped / 60);
  const mins = Math.round(clamped % 60);
  return `${padTimePart(hours)}:${padTimePart(mins)}`;
};

const normalizeIntervalMinutes = (
  value,
  fallback = DEFAULT_BOOKING_SLOT_INTERVAL_MINUTES
) => {
  const numeric = Number(value);
  if (Number.isInteger(numeric) && numeric > 0 && numeric <= 12 * 60) {
    return numeric;
  }
  return fallback;
};

export const formatTimeForDisplay = (value) => {
  const minutes = timeStringToMinutes(value);
  if (minutes === null) return value || "";
  const date = new Date(Date.UTC(2000, 0, 1, 0, 0, 0, 0));
  date.setUTCMinutes(minutes);
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  }).format(date);
};

export const formatSlotForDisplay = (slot) => {
  const normalized = normalizeSlot(slot);
  if (!normalized) return "Time TBD";
  if (normalized.start && normalized.end) {
    return `${formatTimeForDisplay(normalized.start)} - ${formatTimeForDisplay(normalized.end)}`;
  }
  return formatTimeForDisplay(normalized.start || normalized.end || "");
};

export const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return "";
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
  }).format(parsed);
};

export const formatDateAndSlotForDisplay = (isoDate, slot) => {
  const datePart = formatDateForDisplay(isoDate);
  const slotPart = normalizeSlot(slot) ? formatSlotForDisplay(slot) : "";
  return [datePart, slotPart].filter(Boolean).join(", ");
};

export const groupBlockedTimesByDate = (blockedTimes = []) => {
  const map = new Map();
  blockedTimes.forEach((block) => {
    const iso = toISODateString(block.date);
    if (!iso) return;
    const normalized = normalizeSlot(block);
    if (!normalized) return;
    const entry = { ...block, ...normalized, isoDate: iso };
    if (!map.has(iso)) {
      map.set(iso, [entry]);
    } else {
      map.get(iso).push(entry);
    }
  });
  for (const [, list] of map) {
    list.sort((a, b) => timeStringToMinutes(a.start) - timeStringToMinutes(b.start));
  }
  return map;
};

export const combineDateAndTime = (isoDate, time) => {
  if (!isoDate || !time) return null;
  const normalizedDate = toISODateString(isoDate);
  if (!normalizedDate) return null;
  const [hours, minutes] = time.split(":");
  if (hours === undefined || minutes === undefined) return null;
  return `${normalizedDate}T${padTimePart(hours)}:${padTimePart(minutes)}:00`;
};

const subtractInterval = (interval, block) => {
  if (!interval) return [];
  if (!block) return [interval];
  if (block.end <= interval.start || block.start >= interval.end) {
    return [interval];
  }
  const segments = [];
  if (block.start > interval.start) {
    segments.push({ start: interval.start, end: Math.min(block.start, interval.end) });
  }
  if (block.end < interval.end) {
    segments.push({ start: Math.max(block.end, interval.start), end: interval.end });
  }
  return segments.filter((segment) => segment.end > segment.start);
};

const toIntervals = (slots = []) =>
  slots
    .map(normalizeSlot)
    .filter(Boolean)
    .map(({ start, end }) => ({
      start: timeStringToMinutes(start),
      end: timeStringToMinutes(end),
    }))
    .filter((interval) => interval.start !== null && interval.end !== null && interval.end > interval.start);

const mergeIntervals = (intervals = []) => {
  const sorted = [...intervals].sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    return a.end - b.end;
  });

  const merged = [];
  sorted.forEach((interval) => {
    if (!merged.length) {
      merged.push({ ...interval });
      return;
    }

    const last = merged[merged.length - 1];
    if (interval.start <= last.end) {
      last.end = Math.max(last.end, interval.end);
      return;
    }

    merged.push({ ...interval });
  });

  return merged;
};

const normalizeBlocksToIntervals = (blocks = []) =>
  blocks
    .map((block) => {
      const normalized = normalizeSlot(block);
      if (!normalized) return null;
      return {
        start: timeStringToMinutes(normalized.start),
        end: timeStringToMinutes(normalized.end),
      };
    })
    .filter((interval) => interval && interval.start !== null && interval.end !== null && interval.end > interval.start);

export const subtractBlocksFromSlots = (slots = [], blocks = []) => {
  const intervals = toIntervals(slots);
  const blockIntervals = normalizeBlocksToIntervals(blocks);
  const reduced = blockIntervals.reduce(
    (current, block) => current.flatMap((interval) => subtractInterval(interval, block)),
    intervals
  );
  return reduced.map((interval) => ({
    start: minutesToTimeString(interval.start),
    end: minutesToTimeString(interval.end),
  }));
};

const overlaps = (slot, block) => {
  const slotInterval = normalizeBlocksToIntervals([slot])[0];
  const blockInterval = normalizeBlocksToIntervals([block])[0];
  if (!slotInterval || !blockInterval) return false;
  return slotInterval.start < blockInterval.end && blockInterval.start < slotInterval.end;
};

export const filterSlotsAgainstBlocks = (slots = [], blocks = []) => {
  const normalizedBlocks = blocks.map(normalizeSlot).filter(Boolean);
  return slots
    .map(normalizeSlot)
    .filter(Boolean)
    .filter((slot) => !normalizedBlocks.some((block) => overlaps(slot, block)));
};

// The length a booking was made at, in minutes, or null if it never saved a usable
// one. Rescheduling offers times at this length, so a service made longer since
// doesn't stretch the booking. Matches the server's resolveBookingDurationMinutes.
export const getBookedDurationMinutes = (booking) => {
  const minutes = Number(booking?.durationSnapshot);
  return Number.isInteger(minutes) && minutes > 0 && minutes <= 24 * 60 ? minutes : null;
};

export const generateServiceSlotsFromAvailability = (
  slots = [],
  {
    serviceDurationMinutes,
    intervalMinutes = DEFAULT_BOOKING_SLOT_INTERVAL_MINUTES,
  } = {}
) => {
  const duration = Number(serviceDurationMinutes);
  if (!Number.isInteger(duration) || duration <= 0) return [];

  const step = normalizeIntervalMinutes(intervalMinutes);
  const seen = new Set();
  const generated = [];
  const intervals = mergeIntervals(toIntervals(slots));

  intervals.forEach((interval) => {
    const latestStart = interval.end - duration;
    for (let start = interval.start; start <= latestStart; start += step) {
      const end = start + duration;
      if (end > 24 * 60 - 1) continue;
      const normalizedSlot = {
        start: minutesToTimeString(start),
        end: minutesToTimeString(end),
      };
      if (!normalizedSlot.start || !normalizedSlot.end) continue;
      const key = `${normalizedSlot.start}-${normalizedSlot.end}`;
      if (seen.has(key)) continue;
      seen.add(key);
      generated.push(normalizedSlot);
    }
  });

  generated.sort((a, b) => {
    const aStart = timeStringToMinutes(a.start);
    const bStart = timeStringToMinutes(b.start);
    if (aStart !== bStart) return aStart - bStart;
    return timeStringToMinutes(a.end) - timeStringToMinutes(b.end);
  });

  return generated;
};

export const buildBusinessHoursWithBlockedTimes = (
  availability = [],
  blockedTimes = [],
  { lookAheadDays = 180, lookBackDays = 30 } = {}
) => {
  const weeklyMap = new Map();
  availability.forEach((entry) => {
    const dow = DAY_INDEX[entry.day];
    if (dow === undefined) return;
    const slots = entry.slots?.map(normalizeSlot).filter(Boolean) ?? [];
    if (!slots.length) return;
    weeklyMap.set(dow, slots);
  });

  if (!weeklyMap.size) return [];

  const blockedByDate = groupBlockedTimesByDate(blockedTimes);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const windows = [];

  for (let offset = -lookBackDays; offset <= lookAheadDays; offset += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() + offset);
    const isoDate = toISODateString(date);
    const daySlots = weeklyMap.get(date.getDay());
    if (!isoDate || !daySlots?.length) continue;
    const blocksForDate = blockedByDate.get(isoDate) ?? [];
    const available = subtractBlocksFromSlots(daySlots, blocksForDate);
    available.forEach((slot) => {
      const start = combineDateAndTime(isoDate, slot.start);
      const end = combineDateAndTime(isoDate, slot.end);
      if (start && end) {
        windows.push({ start, end });
      }
    });
  }

  return windows;
};

export { DAY_NAMES, DAY_INDEX };
